import React, { useEffect, useRef, useState } from 'react';
import { colors, fonts, radius } from './theme';

/**
 * PWA chrome: offline banner, SW update toast, install prompt (Android + iOS).
 * Mount once near the root of the app.
 *
 * Props:
 *   topOffset (number, default 0): pixels of fixed UI above this banner —
 *     used to stack the SW update banner directly under the impersonation /
 *     event-alert banners (RW-175).
 *   onUpdateBannerVisibilityChange (fn): called with `true` when the SW
 *     update banner appears so App.js can offset everything below it.
 */
export default function PWAShell({ topOffset = 0, onUpdateBannerVisibilityChange }) {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showOnlineToast, setShowOnlineToast] = useState(false);
  const [offlineDismissed, setOfflineDismissed] = useState(false);

  // SW update
  const [updateReady, setUpdateReady] = useState(false);
  const waitingRef = useRef(null);

  // Install prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [iosInstall, setIosInstall] = useState(false);

  // ── Online/offline ───────────────────────────────────────────────
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setOfflineDismissed(false);
      setShowOnlineToast(true);
      setTimeout(() => setShowOnlineToast(false), 3000);
      // Flush queued offline messages
      try {
        const raw = localStorage.getItem('offlineMessageQueue');
        const queue = raw ? JSON.parse(raw) : [];
        if (queue.length > 0) {
          window.dispatchEvent(new CustomEvent('reve:flushOfflineQueue', { detail: queue }));
        }
      } catch {}
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ── Service worker registration + update detection ───────────────
  // RW-167 (revisit): the original strategy showed the banner the moment a
  // fresh updatefound→installed transition completed. That was too eager:
  // every page reload after a deploy fired updatefound, so users saw the
  // banner over and over even after dismissing.
  //
  // New rule: the banner only appears on a SUBSEQUENT visit, never the visit
  // that detected the new worker. We persist the new SW's content hash to
  // localStorage on install; on the next page load (a NEW session) we show
  // the banner only if registration.waiting still exists AND the dismissed
  // hash is different. sessionStorage gates whether this is a fresh tab.
  //
  // Storage keys:
  //   localStorage.sw_pending_update_hash  — set on updatefound→installed
  //   localStorage.sw_version_dismissed_hash — set on user dismiss/refresh
  //   sessionStorage.pwa_session_started   — marks the current tab/session
  const newHashRef = useRef('');
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const getDismissedHash = () => {
      try { return localStorage.getItem('sw_version_dismissed_hash') || ''; }
      catch { return ''; }
    };
    const getPendingHash = () => {
      try { return localStorage.getItem('sw_pending_update_hash') || ''; }
      catch { return ''; }
    };
    const setPendingHash = (h) => {
      try { localStorage.setItem('sw_pending_update_hash', h); } catch {}
    };

    // Mark that this session has started. If the flag was already present,
    // this is NOT a fresh session — but we treat the first useEffect run
    // as the initial check regardless. We only use sessionStorage to make
    // sure mid-session updatefound events do NOT immediately show the banner.
    const sessionAlreadyStarted = (() => {
      try { return sessionStorage.getItem('pwa_session_started') === '1'; }
      catch { return false; }
    })();
    try { sessionStorage.setItem('pwa_session_started', '1'); } catch {}

    const hashSwFile = async () => {
      try {
        const res = await fetch('/sw.js', { cache: 'no-cache' });
        const txt = await res.text();
        let h = 0;
        for (let i = 0; i < txt.length; i++) { h = (h * 31 + txt.charCodeAt(i)) | 0; }
        return String(h);
      } catch { return ''; }
    };

    let mounted = true;

    navigator.serviceWorker.register('/sw.js').then((reg) => {
      if (!mounted) return;

      // 1) Subsequent-visit reveal: if a waiting SW already exists when this
      //    page loads (because a previous session detected it), show the
      //    banner — but ONLY if the user hasn't dismissed this exact worker.
      const checkInitialWaiting = async () => {
        if (sessionAlreadyStarted) return; // mid-session re-mount, ignore
        if (!reg.waiting) return;
        const hash = await hashSwFile();
        if (!mounted || !hash) return;
        const pending = getPendingHash();
        const dismissed = getDismissedHash();
        // Require all three: a waiting worker, a pending hash recorded by a
        // previous session, AND the user has not dismissed this hash yet.
        if (!pending || pending !== hash) return;
        if (dismissed === hash) return;
        newHashRef.current = hash;
        waitingRef.current = reg.waiting;
        setUpdateReady(true);
      };
      checkInitialWaiting();

      // 2) Mid-session updatefound: when a new SW finishes installing in
      //    THIS session, just record its hash to localStorage. Do NOT show
      //    the banner now — the user will see it on their next visit. This
      //    is the key change that stops the banner from popping after every
      //    deploy refresh.
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', async () => {
          if (nw.state !== 'installed') return;
          if (!navigator.serviceWorker.controller) return; // first install — no banner ever
          const hash = await hashSwFile();
          if (!mounted || !hash) return;
          // Record for the next session; clear any dismiss for the previous
          // hash so the new update is eligible to surface next visit.
          if (getPendingHash() !== hash) {
            setPendingHash(hash);
            const dismissed = getDismissedHash();
            if (dismissed && dismissed !== hash) {
              try { localStorage.removeItem('sw_version_dismissed_hash'); } catch {}
            }
          }
        });
      });
    }).catch(() => {});

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      // The new SW just took control — wipe BOTH update flags so we start
      // clean on the post-reload page (which is now running the new code).
      try {
        localStorage.removeItem('sw_pending_update_hash');
        localStorage.removeItem('sw_version_dismissed_hash');
      } catch {}
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    // navigator.serviceWorker outlives this component — without the remove,
    // every remount (and StrictMode's dev double-mount) stacks another
    // listener, each with its own `refreshing` latch.
    return () => {
      mounted = false;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // Notify App.js when the SW update banner appears/disappears so it can
  // stack the notification-permission banner directly underneath (RW-175).
  useEffect(() => {
    if (typeof onUpdateBannerVisibilityChange === 'function') {
      onUpdateBannerVisibilityChange(updateReady);
    }
  }, [updateReady, onUpdateBannerVisibilityChange]);

  const recordDismiss = () => {
    try {
      const hash = newHashRef.current;
      if (hash) localStorage.setItem('sw_version_dismissed_hash', hash);
    } catch {}
  };

  const applyUpdate = () => {
    recordDismiss();
    // Clear the pending flag too — the worker is about to activate, so the
    // refreshed page should not see this hash as "pending" anymore.
    try { localStorage.removeItem('sw_pending_update_hash'); } catch {}
    try { waitingRef.current?.postMessage({ type: 'SKIP_WAITING' }); } catch {}
    // Note: do NOT also reload here — controllerchange will handle it.
    setUpdateReady(false);
  };

  const dismissUpdate = () => {
    recordDismiss();
    setUpdateReady(false);
  };

  // ── Install prompt (Android / desktop Chromium) ──────────────────
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) {
      try {
        const { trackEvent } = require('../lib/analytics'); // eslint-disable-line global-require
        trackEvent('pwa_session_started');
      } catch {}
      return;
    }
    const dismissedRaw = (() => { try { return localStorage.getItem('installPromptDismissed'); } catch { return null; } })();
    const dismissedAt = dismissedRaw ? Number(dismissedRaw) : 0;
    const sevenDays = 7 * 86400000;
    const recentlyDismissed = dismissedAt && (Date.now() - dismissedAt) < sevenDays;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!recentlyDismissed) setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS detection — Safari doesn't fire beforeinstallprompt
    const ua = navigator.userAgent || '';
    const isIos = /(iPad|iPhone|iPod)/.test(ua) && !window.MSStream;
    if (isIos && !recentlyDismissed) setIosInstall(true);

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismissInstall = () => {
    try { localStorage.setItem('installPromptDismissed', String(Date.now())); } catch {}
    setShowInstall(false);
    setIosInstall(false);
  };

  const acceptInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice && choice.outcome === 'accepted') {
      try { const { trackEvent } = require('../lib/analytics'); trackEvent('app_installed'); } catch {} // eslint-disable-line global-require
    }
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  // ── Staging environment indicator ────────────────────────────────
  const isStaging = (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').includes('staging');

  // ── Render ───────────────────────────────────────────────────────
  return (
    <>
      {isStaging ? (
        <div role="status" style={{
          position: 'fixed', top: 'var(--reve-safe-top)', left: 0, right: 0, height: 24,
          background: '#FEF3C7', color: '#0F1011',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: fonts.body, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
          borderBottom: '1px solid #FBBF24', zIndex: 10000,
        }}>
          ⚠️ STAGING ENVIRONMENT
        </div>
      ) : null}
      {/* Offline banner */}
      {!online && !offlineDismissed ? (
        <div role="status" style={{
          position: 'sticky', top: 0, zIndex: 9000,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', background: '#F3F4F6', color: colors.noir,
          fontFamily: fonts.body, fontSize: 13, borderBottom: '1px solid #E5E7EB',
        }}>
          <span aria-hidden>☁️</span>
          <span style={{ flex: 1 }}>You're offline. Some features may be limited.</span>
          <button type="button" onClick={() => setOfflineDismissed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: colors.gray2, padding: 4 }}
            aria-label="Dismiss">✕</button>
        </div>
      ) : null}

      {/* Back-online toast */}
      {showOnlineToast ? (
        <div role="status" style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          padding: '8px 16px', background: '#10B981', color: '#fff', borderRadius: radius.sm,
          fontFamily: fonts.body, fontSize: 13, fontWeight: 600, zIndex: 9999,
          boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
        }}>
          You're back online!
        </div>
      ) : null}

      {/* SW update banner — full-width strip that stacks under impersonation
          / event-alert banners and pushes the rest of the UI down. RW-175. */}
      {updateReady ? (
        <div role="alert" style={{
          position: 'fixed', top: topOffset, left: 0, right: 0, height: 44,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          background: '#6366F1', color: '#fff', padding: '0 16px',
          fontFamily: fonts.body, fontSize: 13, fontWeight: 500,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 1099,
        }}>
          <span>A new version of Rêve Workspace is available.</span>
          <button type="button" onClick={applyUpdate}
            style={{ padding: '5px 12px', borderRadius: radius.sm, border: '1.5px solid #fff', background: 'transparent', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: fonts.body }}>
            Refresh
          </button>
          <button type="button" onClick={dismissUpdate}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, opacity: 0.85 }} aria-label="Dismiss">✕</button>
        </div>
      ) : null}

      {/* Install banner (Android/desktop) */}
      {showInstall ? (
        <div role="dialog" aria-label="Install Rêve Workspace" style={{
          position: 'fixed', bottom: 80, left: 12, right: 12, maxWidth: 420, margin: '0 auto',
          background: colors.white, borderRadius: radius.lg, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, zIndex: 8500,
          border: `1px solid ${colors.pearl}`,
        }}>
          <img src="/icons/icon-96x96.png" alt="" width={40} height={40} style={{ borderRadius: 8 }} loading="lazy" />
          <div style={{ flex: 1, minWidth: 0, fontFamily: fonts.body, fontSize: 13, color: colors.noir }}>
            <strong style={{ display: 'block' }}>Install Rêve Workspace</strong>
            <span style={{ color: colors.gray2 }}>Quick access from your home screen.</span>
          </div>
          <button type="button" onClick={acceptInstall}
            style={{ padding: '8px 14px', borderRadius: radius.sm, border: 'none', background: colors.coral, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: fonts.body, cursor: 'pointer' }}>
            Install
          </button>
          <button type="button" onClick={dismissInstall}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: colors.gray2, padding: 4 }} aria-label="Dismiss">✕</button>
        </div>
      ) : null}

      {/* iOS install instructions */}
      {iosInstall && !showInstall ? (
        <div role="dialog" aria-label="Install Rêve Workspace" style={{
          position: 'fixed', bottom: 80, left: 12, right: 12, maxWidth: 420, margin: '0 auto',
          background: colors.white, borderRadius: radius.lg, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, zIndex: 8500,
          border: `1px solid ${colors.pearl}`,
        }}>
          <img src="/icons/icon-96x96.png" alt="" width={40} height={40} style={{ borderRadius: 8 }} loading="lazy" />
          <div style={{ flex: 1, minWidth: 0, fontFamily: fonts.body, fontSize: 12, color: colors.noir, lineHeight: 1.4 }}>
            <strong style={{ display: 'block' }}>Install Rêve Workspace</strong>
            <span style={{ color: colors.gray2 }}>Tap <span aria-label="Share">⎙</span> Share, then <strong>Add to Home Screen</strong>.</span>
          </div>
          <button type="button" onClick={dismissInstall}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: colors.gray2, padding: 4 }} aria-label="Dismiss">✕</button>
        </div>
      ) : null}
    </>
  );
}
